import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isEntraAuth, msalInstance } from '../../auth/authConfig';
import splashVideo from '../../assets/dawn_splash_2.mp4';
import wmLogo from '../../assets/logos/WM-Logo-CB-White-Header.png';
import { CheckCircle, Loader } from 'lucide-react';

const STEPS = [
  { key: 'connecting',     label: 'Connecting to Microsoft'  },
  { key: 'authenticating', label: 'Signing you in'           },
  { key: 'verifying',      label: 'Verifying your access'    },
  { key: 'ready',          label: 'Loading your workspace'   },
];
const STEP_ORDER = ['connecting', 'authenticating', 'verifying', 'ready'];

const AuthGate = ({ children }) => {
  const location  = useLocation();
  const { authStep } = useAuth();

  const isInvitePage = location.pathname.startsWith('/accept-invitation');
  const alreadyShown = sessionStorage.getItem('dawn-splash-shown') === 'true';
  // Skip video on the pre-auth redirect load (no MSAL accounts = about to be sent to Azure AD).
  // Show it only when returning with an authenticated session.
  const noAccountsYet = isEntraAuth() && msalInstance && msalInstance.getAllAccounts().length === 0;
  const skipVideo    = isInvitePage || alreadyShown || noAccountsYet;

  const [videoPhase,   setVideoPhase]   = useState(!skipVideo);
  const [gateExiting,  setGateExiting]  = useState(false);
  const [gateDone,     setGateDone]     = useState(false);

  const exitGate = useCallback(() => {
    setGateExiting(true);
    setTimeout(() => setGateDone(true), 500);
  }, []);

  const completeVideo = useCallback(() => {
    sessionStorage.setItem('dawn-splash-shown', 'true');
    if (!isEntraAuth() || authStep === 'ready') {
      // Impersonation mode, or auth already resolved while video played — skip step card
      exitGate();
    } else {
      setVideoPhase(false);
    }
  }, [exitGate, authStep]);

  // Fallback: abort video after 60s (full-speed playback safety net)
  useEffect(() => {
    if (!videoPhase) return;
    const t = setTimeout(completeVideo, 60000);
    return () => clearTimeout(t);
  }, [videoPhase, completeVideo]);

  // Exit gate once auth is resolved and we're past the video phase
  useEffect(() => {
    if (!videoPhase && authStep === 'ready' && !gateExiting) {
      exitGate();
    }
  }, [videoPhase, authStep, gateExiting, exitGate]);

  // Invite page manages its own loading UI
  if (isInvitePage) return children;
  if (gateDone)     return children;

  const currentStepIdx = STEP_ORDER.indexOf(authStep);

  return (
    <>
      {/* Pre-render app content behind the backdrop during exit so its background
          is already present when the overlay fades — avoids body-color flash */}
      {gateExiting && children}
      <div className={`auth-gate-backdrop${gateExiting ? ' auth-gate-exiting' : ''}`}>
      <div className="auth-gate-card">
        {videoPhase ? (
          <video
            className="auth-gate-video"
            autoPlay
            muted
            playsInline
            onCanPlay={e => { e.target.playbackRate = 2; }}
            onEnded={completeVideo}
          >
            <source src={splashVideo} type="video/mp4" />
          </video>
        ) : (
          <>
            {/* Header */}
            <div className="auth-gate-header">
              <img src={wmLogo} alt="Wunderlich-Malec" className="auth-gate-logo" />
              <div className="auth-gate-header-divider" />
              <div>
                <div className="auth-gate-title">DAWN</div>
                <div className="auth-gate-subtitle">DOCUMENTATION · ASSETS · WIRING · NETWORKS</div>
              </div>
            </div>

            {/* Step indicators */}
            <div className="auth-gate-steps">
              {STEPS.map((step, idx) => {
                const isDone   = authStep === 'ready' || idx < currentStepIdx;
                const isActive = !isDone && idx === currentStepIdx;
                const state    = isDone ? 'done' : isActive ? 'active' : 'pending';
                return (
                  <div key={step.key} className={`auth-gate-step ${state}`}>
                    <div className="auth-gate-step-icon">
                      {isDone ? (
                        <CheckCircle size={20} color="#1a5568" />
                      ) : isActive ? (
                        <Loader size={20} className="auth-gate-spinner" color="#1a5568" />
                      ) : (
                        <div className="auth-gate-step-circle" />
                      )}
                    </div>
                    <span className="auth-gate-step-label">{step.label}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
};

export default AuthGate;
